import { SproutBuddy, DEFAULT_BUDDY } from './SproutBuddy'

/**
 * The front page picture: Sprout Buddy waters letters, and a word plant grows.
 *
 * From Sam's "watering_word_plant" sheet, redrawn as shapes (8 Oct 2026).
 * Sam's direction for the motion: "it looks like the buddy is watering
 * letters and then the tree gradually grows to the current state, then sways
 * a bit. This communicates the sprout-grows process." So it plays once, in
 * order, about six and a half seconds (slowed on 8 Oct at Sam's request,
 * from about four):
 *
 *   0.0 s   letters fall from the can into the soil
 *   1.2 s   the stem grows up out of the soil
 *   3.0 s   the six leaves open one by one, lowest first
 *   6.4 s   the finished plant starts its gentle sway, and keeps it
 *
 * Anyone whose device asks for less motion gets the finished plant at once,
 * with no letters falling (the media query at the bottom).
 */
const LEAVES: { word: string; x: number; y: number; angle: number; color: string; at: number; size?: number }[] = [
  { word: 'READ', x: 104, y: 186, angle: 198, color: '#E0457B', at: 3.0 },
  { word: 'WRITE', x: 112, y: 170, angle: -18, color: '#8E5CC9', at: 3.45 },
  { word: 'GROW', x: 102, y: 130, angle: 212, color: '#9BC53D', at: 3.95 },
  { word: 'CREATE', x: 112, y: 144, angle: -30, color: '#B2408F', at: 4.4, size: 1.08 },
  { word: 'LEARN', x: 112, y: 108, angle: -44, color: '#F2C230', at: 4.85 },
  { word: 'SPEAK', x: 108, y: 88, angle: -92, color: '#3E8E6A', at: 5.3 },
]

// The letters that pour from the can, and when each one leaves the spout.
const DROPS = ['W', 'O', 'R', 'D', 'S', 'A', 'B', 'C', 'G', 'R', 'O', 'W']
const LETTER_COLORS = ['#E0457B', '#8E5CC9', '#3E8E6A', '#F2C230', '#4C7BE0', '#E8833A']

const INK = '#2B1D14'
const STEM = 'M110 232 C106 204 116 176 108 148 C102 124 114 104 108 82'

export function WordPlantScene({ width = 360 }: { width?: number }) {
  return (
    <svg viewBox="0 0 360 260" width={width} height={Math.round(width * 260 / 360)}
      role="img" aria-label="Sprout Buddy waters letters into the soil, and a plant grows whose leaves say read, write, grow, create, learn and speak"
      style={{ display: 'block', maxWidth: '100%', height: 'auto', overflow: 'visible' }}>
      <style>{`
        .ws-letter { opacity: 0; animation: ws-letter-fall 1.7s ease-in both; }
        @keyframes ws-letter-fall {
          0%   { transform: translate(0, 0) rotate(0deg); opacity: 0 }
          12%  { opacity: 1 }
          80%  { opacity: 1 }
          100% { transform: translate(-66px, 60px) rotate(-30deg); opacity: 0 }
        }
        .ws-stem { stroke-dasharray: 1; stroke-dashoffset: 0; animation: ws-stem-grow 3.6s 1.2s cubic-bezier(.3,.7,.4,1) both; }
        @keyframes ws-stem-grow { 0% { stroke-dashoffset: 1; opacity: 0 } 4% { opacity: 1 } 100% { stroke-dashoffset: 0; opacity: 1 } }
        .ws-leaf { transform-box: fill-box; transform-origin: 0% 50%; animation: ws-leaf-open 0.8s cubic-bezier(.3,1.6,.5,1) both; }
        @keyframes ws-leaf-open { from { transform: scale(0) } to { transform: scale(1) } }
        .ws-sway { transform-origin: 110px 232px; animation: ws-sway 4s 6.4s ease-in-out infinite; }
        @keyframes ws-sway { 0%,100% { transform: rotate(0deg) } 25% { transform: rotate(-2deg) } 75% { transform: rotate(2deg) } }
        .ws-mound { transform-box: fill-box; transform-origin: 50% 100%; animation: ws-mound 0.9s 0.9s ease-out both; }
        @keyframes ws-mound { from { transform: scaleY(0.2) } to { transform: scaleY(1) } }
        @media (prefers-reduced-motion: reduce) {
          .ws-letter { animation: none; opacity: 0 }
          .ws-stem, .ws-leaf, .ws-sway, .ws-mound { animation: none }
        }
      `}</style>

      {/* soil */}
      <ellipse cx={180} cy={246} rx={178} ry={20} fill="#6B4A2F" />
      <ellipse cx={180} cy={240} rx={160} ry={12} fill="#7B5638" />
      <path className="ws-mound" d="M86 236 C96 224 124 224 134 236 Z" fill="#8A6142" />

      <g className="ws-sway">
        <path className="ws-stem" pathLength={1} d={STEM}
          stroke={INK} strokeWidth={8} fill="none" strokeLinecap="round" />
        <path className="ws-stem" pathLength={1} d={STEM}
          stroke="#3E8E6A" strokeWidth={4.5} fill="none" strokeLinecap="round" />
        {LEAVES.map((l) => {
          const flip = l.angle > 90 || l.angle < -90
          const s = l.size ?? 1
          return (
            <g key={l.word} transform={`translate(${l.x} ${l.y}) rotate(${l.angle}) scale(${s})`}>
              <g className="ws-leaf" style={{ animationDelay: `${l.at}s` }}>
                <path d="M0 0 C14 -17 50 -19 70 0 C50 19 14 17 0 0 Z"
                  fill={l.color} stroke={INK} strokeWidth={2.6} strokeLinejoin="round" />
                <path d="M3 0 L64 0" stroke="rgba(0,0,0,0.18)" strokeWidth={1.5} />
                <text x={37} y={0} transform={flip ? 'rotate(180 37 0)' : undefined}
                  textAnchor="middle" dominantBaseline="central"
                  style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 600, fontSize: 11.5, letterSpacing: 0.4 }}
                  fill={l.word === 'LEARN' ? INK : '#FFFFFF'}>{l.word}</text>
              </g>
            </g>
          )
        })}
      </g>

      {/* the letters, poured from the can */}
      {DROPS.map((ch, i) => (
        <text key={i} className="ws-letter" x={203 - (i % 3) * 4} y={166 + (i % 2) * 4}
          textAnchor="middle"
          style={{
            animationDelay: `${i * 0.38}s`, fontFamily: "'Fredoka', sans-serif",
            fontWeight: 600, fontSize: 13,
          }}
          fill={LETTER_COLORS[i % LETTER_COLORS.length]} stroke={INK} strokeWidth={0.6}
          paintOrder="stroke">{ch}</text>
      ))}

      {/* Sprout Buddy, turned to face the plant */}
      <g transform="translate(352 64) scale(-1 1)">
        <SproutBuddy buddy={{ ...DEFAULT_BUDDY, held: 'can' }} size={150} label="" />
      </g>
    </svg>
  )
}
